import { type MutableRefObject, type ReactNode, useEffect, useRef, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import type { Offer } from '@/interfaces';
import setupLightbox from '@/lib/gallery/lightbox.js';

export type OfferGallery = Pick<Offer, 'hotelId' | 'imagePaths'> & { openGallery: boolean } & {
    imageRef: MutableRefObject<HTMLDivElement | null>;
} & { galleryValue: number };

interface Image {
    srcSet: string;
    thumbSrc: string;
    thumb155hSrc: string;
    description: string;
}

const hasImages = (imagePath: string[]): number => imagePath?.length;

const makeSrcSet = (imageFullPath: string): string => {
    if (imageFullPath?.replace) {
        return (
            `${imageFullPath.replace('/1280w/', '/240x180c/')} 240w, ` +
            `${imageFullPath.replace('/1280w/', '/320x240c/')} 320w, ` +
            `${imageFullPath.replace('/1280w/', '/480x360c/')} 480w, ` +
            `${imageFullPath.replace('/1280w/', '/640x480c/')} 640w, ` +
            `${imageFullPath.replace('/1280w/', '/960x720c/')} 960w`
        );
    }
    return imageFullPath;
};

const imageObject = (image: string): Image => ({
    srcSet: makeSrcSet(image),
    thumbSrc: image.replace('/1280w/', '/300x225c/'),
    thumb155hSrc: image.replace('/1280w/', '/155h/'),
    description: '',
});
const renderImages = (imgPath: string, imagePath: string[], showImages: boolean): ReactNode => {
    const imageUrlPrefix = `${imgPath}1280w`;

    return imagePath.map((_image: string, index: number): ReactNode => {
        const imageFullUrl = `${imageUrlPrefix}${imagePath[index]}`;
        const image = imageObject(imageFullUrl);

        return (
            <img
                // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                key={`hidden-image-${index}`}
                src={showImages ? imageFullUrl : undefined}
                data-src={imageFullUrl}
                data-srcset={image.srcSet}
                sizes="(min-width: 1020px) 650px, (min-width: 768px) 66vw"
                data-thumb-src={image.thumbSrc}
                data-gallerylightbox-name="ad"
                alt="Galleribilde"
            />
        );
    });
};
export const ImageGallery = ({ imagePaths, hotelId, openGallery, imageRef, galleryValue }: OfferGallery) => {
    const { imgPath } = useConfig();
    const [loadGallery, setloadGallery] = useState(openGallery);
    const ele = useRef<HTMLDivElement | null>(null);

    useEffect((): void => {
        if (imageRef.current) {
            setupLightbox(imageRef.current);
        }
        if (openGallery) {
            setupLightbox(ele.current);
            if (ele.current && galleryValue === 0) {
                ele.current.click();
            }
        }
        setloadGallery(false);
    }, [galleryValue, imageRef, openGallery]);

    return (
        <div data-testid="imageGallery" tabIndex={-1}>
            <div ref={ele} data-gallerylightbox-name="ad" data-gallerylightbox-context="mobile" id={`imageGallery_${hotelId}`}>
                {loadGallery ? (
                    <div className="u-display-none" data-testid="galleryLoad">
                        {hasImages(imagePaths) ? renderImages(imgPath, imagePaths, true) : null}
                    </div>
                ) : (
                    <div className="u-display-none">{hasImages(imagePaths) ? renderImages(imgPath, imagePaths, false) : null}</div>
                )}
            </div>
        </div>
    );
};
